
import React, { useEffect, useRef, useState } from "react";
import { IconButton, Box, useTheme } from "@mui/material";
import MailIcon from '@mui/icons-material/Mail';
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useSearchParams } from "react-router-dom";
import { formatDate } from "utils/dateFormats";
import ReactHtmlParser from "html-react-parser";
import { useAuth } from "hooks";
import ShipmentActionDetailDrawer from "./ShipmentActionDetailDrawer";
import { useUpdateShipmentActionMutation } from "store/services/shipmentActionApi";

const ShipmentActionList = ({
  shipmentActions,
  isBusy = false,
  totalAction,
  perPage,
  currentPage,
  handlePageChange,
  handlePerRowsChange,
  onEditAction,
  selectedActionId: propSelectedActionId,
  refetchActions,
}) => {
  const [selectedAction, setSelectedAction] = useState(null);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [manuallyClosedActionId, setManuallyClosedActionId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlActionId = searchParams.get("actionId");
  const selectedActionId = urlActionId || propSelectedActionId;
  const actionRefs = useRef({});
  const { isVendor } = useAuth();
  const [updateShipmentAction] = useUpdateShipmentActionMutation();
  const theme = useTheme();

  // Dark mode aware colors
  const getDarkModeStyles = () => {
    const isDark = theme.palette.mode === 'dark';
    return {
      selectedRow: {
        backgroundColor: isDark ? '#1565c0' : '#e3f2fd',
        borderLeft: `4px solid ${isDark ? '#90caf9' : '#1976d2'}`,
        "&:hover": {
          backgroundColor: isDark ? '#1976d2' : '#bbdefb'
        },
      },
      unreadRow: {
        backgroundColor: isDark ? '#e65100' : '#fff3e0',
        "&:hover": {
          backgroundColor: isDark ? '#ef6c00' : '#ffe0b2'
        },
      },
    };
  };

  useEffect(() => {
    if (selectedActionId && shipmentActions?.length && !isBusy) {
      // Don't auto-open if user manually closed this specific action
      if (manuallyClosedActionId === selectedActionId) {
        return;
      }

      const action = shipmentActions.find((a) => a._id === selectedActionId);
      if (action) {
        setSelectedAction(action);
        setIsDetailDrawerOpen(true);

        if (!action.responseRead && action.status === "Open") {
          updateShipmentAction({ id: action._id, status: "Read" })
            .unwrap()
            .then(() => refetchActions())
            .catch(console.error);
        }

        if (actionRefs.current[selectedActionId]) {
          actionRefs.current[selectedActionId].scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    }
    // Remove the else block that was auto-closing the drawer
  }, [selectedActionId, shipmentActions, isBusy, updateShipmentAction, refetchActions, manuallyClosedActionId]);
  const handleViewDetails = (action) => {
    setSelectedAction(action);
    setIsDetailDrawerOpen(true);
    // Clear the manually closed flag when explicitly opening
    setManuallyClosedActionId(null);

    if (!action.responseRead && action.status === "Open") {
      updateShipmentAction({ id: action._id, status: "Read" })
        .unwrap()
        .then(() => refetchActions())
        .catch(console.error);
    }
  };

  const handleCloseDrawer = () => {
    setIsDetailDrawerOpen(false);
    setSelectedAction(null);

    // Track which action was manually closed
    if (selectedActionId) {
      setManuallyClosedActionId(selectedActionId);
    }

    // Clear URL parameter if present
    if (urlActionId) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("actionId");
      setSearchParams(newSearchParams);
    }
  };

  // Clear manually closed flag when actions are refetched (e.g., after adding new action)
  useEffect(() => {
    if (!isBusy && shipmentActions?.length) {
      setManuallyClosedActionId(null);
    }
  }, [isBusy, shipmentActions]);

  const columns = [
    {
      name: "Action Type",
      selector: (row) => row?.actionType,
      cell: (row) => <div title={row?.actionType}>{row?.actionType}</div>,
      minWidth: "150px",
    },
    {
      name: "Who Can Close",
      selector: (row) => row?.whoCanClose,
      cell: (row) => {
        const displayValue =
          isVendor && row?.whoCanClose === "Vendor"
            ? "Vendor"
            : row?.whoCanClose;

        return <div title={displayValue}>{displayValue}</div>;
      },
      minWidth: "150px",
    },
    ...(!isVendor
      ? [
        {
          name: "Responsible",
          selector: (row) => row?.responsible?.name,
          cell: (row) => (
            <div title={row?.responsible?.name}>
              {row?.responsible?.name || "N/A"}
            </div>
          ),
          minWidth: "150px",
        },
      ]
      : []),
    {
      name: "Type",
      cell: (row) => (row?.systemType
      ),
      minWidth: "140px",
    },
    {
      name: "Status",
      selector: (row) => row?.status,
      cell: (row) => <div title={row?.status}>{row?.status}</div>,
      minWidth: "120px",
    },
    {
      name: "Created By",
      selector: (row) => row?.createdBy?.name,
      cell: (row) => (row?.createdBy?.name
      ),
      minWidth: "140px",
    },
    {
      name: "CreatedAt",
      selector: (row) => row?.createdAt,
      cell: (row) => <div title={row?.createdAt}>{formatDate(row?.createdAt)}</div>,
      minWidth: "140px",
    },
    {
      name: "Description",
      cell: (row) => (
        ReactHtmlParser(row?.description)
      ),
      minWidth: "140px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton size="small" onClick={() => handleViewDetails(row)}>
            <InfoOutlined color="primary" />
          </IconButton>
          {!isVendor && (
            <IconButton size="small" onClick={() => onEditAction(row)}>
              <ModeEditOutlineTwoToneIcon />
            </IconButton>
          )}
          <Box position="relative" display="inline-flex">
            <MailIcon color="action" fontSize="medium" />
            {(row?.responses?.length || 0) > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  backgroundColor: "#1976d2",
                  color: "white",
                  borderRadius: "50%",
                  width: 18,
                  height: 18,
                  fontSize: "0.7rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "medium",
                  zIndex: 1,
                }}
              >
                {row.responses.length}
              </Box>
            )}
          </Box>
        </Box>
      ),
      ignoreRowClick: true,
      right: true,
    }
  ];

  const conditionalRowStyles = [
    {
      when: (row) => row._id === selectedActionId,
      style: getDarkModeStyles().selectedRow,
    },
    {
      when: (row) => !row.responseRead && row.status === "Open",
      style: getDarkModeStyles().unreadRow,
    },
  ];

  return (
    <BreadcrumbLayout.Paper>
      <AppTable
        columns={columns}
        data={shipmentActions || []}
        paginationServer
        paginationPerPage={perPage}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        progressPending={isBusy}
        paginationTotalRows={totalAction}
        conditionalRowStyles={conditionalRowStyles}
        rowRef={(row) => (actionRefs.current[row._id] = row)}
      />
      <ShipmentActionDetailDrawer
        open={isDetailDrawerOpen}
        onClose={handleCloseDrawer}
        action={selectedAction}
        refetchActions={refetchActions}
      />
    </BreadcrumbLayout.Paper>
  );
};

export default ShipmentActionList;